import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

// ErrorCode enum values from @gpstrackit/px-api-framework
const ErrorCode = {
    UNAUTHORIZED: 401,
    RECORD_NOT_EXIST: 1002,
    DUPLICATED_RECORD: 1010,
    INVALID_PARAMS: 1011,
};

const SLICE_NAME = 'dealers/views/.components/createEditWebHook/redux';

const INIT_STATE = {
    loading: false,
    openModal: false,
    result: null,
    dealerId: null,
    webhook: null,
    error: null,
};

export const createDealerWebhook = createAsyncThunk(
    `${SLICE_NAME}/createDealerWebhook`,
    async (payload, { rejectWithValue }) => {
        try {
            const { dealerId, name, url, eventType, headers, status } = payload;

            const response = await clientMutation(
                `mutation createDealerWebhook($dealerId: Int!, $name: String!, $url: String!, $eventType: EventTypeInput!, $headers: AWSJSON, $status: Int!) {
                    result: createDealerWebhook(dealerId: $dealerId, name: $name, url: $url, eventType: $eventType, headers: $headers, status: $status) {
                        id dealerId name url eventType status createdBy createdOn updatedBy updatedOn
                    }
                }`,
                { dealerId, name, url, eventType, headers, status },
                endpoints.GRAPHQL_GENERAL
            );

            return { webhook: response?.result, success: !!response?.result };
        } catch (exc) {
            ExceptionManager(exc, `${SLICE_NAME}/createDealerWebhook`, 'createDealerWebhook');

            // Extract errorCode from GraphQL error response
            let errorCode = null;
            let errorMessage = exc.error || exc.message || 'Failed to create webhook';

            if (exc.errorData?.errors?.[0]?.errorInfo?.code) {
                errorCode = Number(exc.errorData.errors[0].errorInfo.code);
                errorMessage = exc.errorData.errors[0].message || errorMessage;
            } else if (exc.innerError?.graphQLErrors?.[0]?.errorInfo?.code) {
                errorCode = Number(exc.innerError.graphQLErrors[0].errorInfo.code);
                errorMessage = exc.innerError.graphQLErrors[0].message || errorMessage;
            }

            return rejectWithValue({
                message: errorMessage,
                errorCode: errorCode,
            });
        }
    }
);

export const updateDealerWebhook = createAsyncThunk(
    `${SLICE_NAME}/updateDealerWebhook`,
    async (payload, { rejectWithValue }) => {
        try {
            const { dealerId, id, name, url, eventType, headers, status } = payload;

            const response = await clientMutation(
                `mutation updateDealerWebhook($dealerId: Int!, $id: Int!, $name: String, $url: String, $eventType: EventTypeInput, $headers: AWSJSON, $status: Int) {
                    result: updateDealerWebhook(dealerId: $dealerId, id: $id, name: $name, url: $url, eventType: $eventType, headers: $headers, status: $status) {
                        id dealerId name url eventType status createdBy createdOn updatedBy updatedOn
                    }
                }`,
                { dealerId, id, name, url, eventType, headers, status },
                endpoints.GRAPHQL_GENERAL
            );

            return { webhook: response?.result, success: !!response?.result };
        } catch (exc) {
            ExceptionManager(exc, `${SLICE_NAME}/updateDealerWebhook`, 'updateDealerWebhook');

            // Extract errorCode from GraphQL error response
            let errorCode = null;
            let errorMessage = exc.error || exc.message || 'Failed to update webhook';


            // Try to extract errorCode from errorData response (errorInfo.code structure)
            if (exc.errorData?.errors?.[0]?.errorInfo?.code) {
                errorCode = Number(exc.errorData.errors[0].errorInfo.code);
                errorMessage = exc.errorData.errors[0].message || errorMessage;
            } else if (exc.innerError?.graphQLErrors?.[0]?.errorInfo?.code) {
                errorCode = Number(exc.innerError.graphQLErrors[0].errorInfo.code);
                errorMessage = exc.innerError.graphQLErrors[0].message || errorMessage;
            }

            return rejectWithValue({
                message: errorMessage,
                errorCode: errorCode,
            });
        }
    }
);

export const fetchWebhookDetails = createAsyncThunk(
    `${SLICE_NAME}/fetchWebhookDetails`,
    async (payload, { rejectWithValue }) => {
        try {
            const { dealerId, webhookId } = payload;

            const response = await clientQuery(
                `query getDealerWebhook ($dealerId: Int!, $id: Int!) {
                    result: getDealerWebhook (dealerId: $dealerId, id: $id) {
                        id dealerId name url eventType headers status createdOn createdBy updatedOn updatedBy
                    }
                }`,
                { dealerId, id: webhookId },
                endpoints.GRAPHQL_GENERAL
            );

            return { webhook: response?.result };
        } catch (exc) {
            ExceptionManager(exc, `${SLICE_NAME}/fetchWebhookDetails`, 'fetchWebhookDetails');
            return rejectWithValue({
                message: exc.error || exc.message || 'Failed to fetch webhook details',
            });
        }
    }
);

export const createEditWebHookSlice = createSlice({
    name: SLICE_NAME,
    initialState: INIT_STATE,
    reducers: {
        openModalCreateEditWebHook(state, action) {
            state.openModal = true;
            state.dealerId = action.payload?.dealerId || null;
            state.webhook = action.payload?.webhook || null;
            state.error = null;
            state.result = null;
        },
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createDealerWebhook.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createDealerWebhook.fulfilled, (state, action) => {
                state.loading = false;
                state.result = action.payload;
            })
            .addCase(createDealerWebhook.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateDealerWebhook.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateDealerWebhook.fulfilled, (state, action) => {
                state.loading = false;
                state.result = action.payload;
            })
            .addCase(updateDealerWebhook.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchWebhookDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWebhookDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.webhook = action.payload?.webhook;
            })
            .addCase(fetchWebhookDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { openModalCreateEditWebHook, reset } = createEditWebHookSlice.actions;
export default createEditWebHookSlice.reducer;
